<template>
    <div ref="templateRoot" class="pdf-container"
        style="background-color: #ffffff !important; color: #000000 !important; isolation: isolate;">

        <!-- 1. NAGŁÓWEK GŁÓWNY (Daty/miejsce + Logo) -->
        <div class="d-flex justify-content-between align-items-start mb-3">
            <div class="header-dates">
                <div class="date-row">
                    <span class="date-label">Miejsce wystawienia:</span>
                    <span class="date-dots"></span>
                    <span class="date-value">{{ invoice.city || '---' }}</span>
                </div>
                <div class="date-row">
                    <span class="date-label">Data wystawienia:</span>
                    <span class="date-dots"></span>
                    <span class="date-value">{{ invoice.createDate || '---' }}</span>
                </div>
                <div class="date-row">
                    <span class="date-label">Data/godzina sprzedaży:</span>
                    <span class="date-dots"></span>
                    <span class="date-value">{{ invoice.sellDate ? invoice.sellDate.replace('T', ' ') : '---' }}</span>
                </div>
            </div>

            <!-- Logo po prawej -->
            <div class="header-logo text-end">
                <img v-if="invoice.sellerLogoUrl" :src="invoice.sellerLogoUrl" alt="Logo"
                    style="max-height: 55px; max-width: 220px; object-fit: contain;" />
                <h3 v-else class="fw-bold text-danger m-0 text-lowercase fs-4">{{ invoice.seller?.companyName ||
                    'autokutno' }}</h3>
            </div>
        </div>

        <!-- 2. TYTUŁ DOKUMENTU -->
        <div class="text-center my-3">
            <h4 class="fw-normal fs-3 m-0 text-uppercase">Faktura {{ invoice.typeOfDocument || 'marża' }} nr {{
                invoice.invoiceNumber || '---' }}</h4>
        </div>

        <!-- 3. BLOK: ZDJĘCIE + SPRZEDAWCA + KUPUJĄCY -->
        <div class="row g-2 mb-3 align-items-stretch">
            <!-- Zdjęcie pojazdu -->
            <div class="col-4">
                <div class="photo-box border h-100 d-flex align-items-center justify-content-center bg-light">
                    <img v-if="carImageUrl" :src="carImageUrl" alt="Zdjęcie pojazdu" class="img-fluid rounded"
                        style="max-height: 140px; width: 100%; object-fit: cover;" />
                    <div v-else class="text-muted small text-center p-3">
                        <i class="bi bi-car-front fs-2 d-block mb-1"></i>
                        [Brak zdjęcia]
                    </div>
                </div>
            </div>

            <!-- Sprzedawca -->
            <div class="col-5">
                <div class="card h-100 border rounded-0">
                    <div class="card-header bg-gray py-1 px-2 fw-normal fs-7 border-bottom">Sprzedawca</div>
                    <div class="card-body p-2 fs-7 line-height-sm">
                        <div class="fw-bold">{{ invoice.seller?.companyName || formatName(invoice.seller) }}</div>
                        <div v-if="invoice.seller?.nip">NIP: {{ invoice.seller.nip }}<span
                                v-if="invoice.seller?.regon">, REGON: {{ invoice.seller.regon }}</span></div>
                        <div v-if="invoice.seller?.street">{{ invoice.seller.street }}</div>
                        <div v-if="invoice.seller?.city">{{ invoice.seller.postalCode }} {{ invoice.seller.city }}</div>
                        <div v-if="invoice.seller?.country">Kraj: {{ invoice.seller.country }}</div>
                        <div v-if="invoice.seller?.email">E-mail: {{ invoice.seller.email }}</div>
                        <div v-if="invoice.seller?.phone">Tel: {{ invoice.seller.phone }}</div>
                    </div>
                </div>
            </div>

            <!-- Kupujący -->
            <div class="col-3">
                <div class="card h-100 border rounded-0">
                    <div class="card-header bg-gray py-1 px-2 fw-normal fs-7 border-bottom">Kupujący</div>
                    <div class="card-body p-2 fs-7 line-height-sm">
                        <div class="fw-bold">{{ formatName(invoice.buyer) }}</div>
                        <div v-if="invoice.buyer?.pesel">Pesel: {{ invoice.buyer.pesel }}</div>
                        <div v-if="invoice.buyer?.nip">NIP: {{ invoice.buyer.nip }}</div>
                        <div v-if="invoice.buyer?.street">{{ invoice.buyer.street }}</div>
                        <div v-if="invoice.buyer?.city">{{ invoice.buyer.postalCode }} {{ invoice.buyer.city }}</div>
                        <div v-if="invoice.buyer?.email">E-mail: {{ invoice.buyer.email }}</div>
                        <div v-if="invoice.buyer?.phone">Tel: {{ invoice.buyer.phone }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 4. TABELA POZYCJI FAKTURY (z tablicy positions) -->
        <table class="table table-bordered custom-table mb-2">
            <thead>
                <tr class="bg-gray">
                    <th style="width: 35px;" class="text-center">LP</th>
                    <th>Nazwa towaru / usługi</th>
                    <th style="width: 45px;" class="text-center">Jm.</th>
                    <th style="width: 45px;" class="text-center">Ilość</th>
                    <th style="width: 95px;" class="text-end">Kwota</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in (invoice.positions || [])" :key="index">
                    <td class="text-center align-middle">{{ index + 1 }}</td>
                    <td>
                        <div class="fw-bold">{{ item.name }}</div>
                        <!-- Szczegóły pojazdu z obiektu car dla pierwszej pozycji -->
                        <div v-if="index === 0 && invoice.car">
                            <div>Kategoria: {{ invoice.car.type || 'Osobowe' }}</div>
                            <div>
                                Pojemność silnika: {{ invoice.car.engineCapacity || '---' }}
                                <span v-if="invoice.car.mileage">, Stan licznika: {{ invoice.car.mileage }}</span>
                            </div>
                            <div>
                                Typ paliwa: {{ invoice.car.fuelType || '---' }}
                                <span v-if="invoice.car.powerHp">, Moc: {{ invoice.car.powerHp }}</span>
                                <span v-if="invoice.car.color">, Kolor: {{ invoice.car.color }}</span>
                            </div>
                            <div>VIN: {{ invoice.car.vin || '---' }}</div>
                            <div>Numer rejestracyjny pojazdu: {{ invoice.car.registrationPlate || '---' }}</div>
                            <div>
                                Rok produkcji: {{ invoice.car.productionYear || '---' }}
                                <span v-if="invoice.car.firstRegistrationDate">, Data pierwszej rejestracji: {{
                                    invoice.car.firstRegistrationDate }}</span>
                            </div>
                        </div>
                    </td>
                    <td class="text-center align-middle">{{ item.unit || 'szt.' }}</td>
                    <td class="text-center align-middle">{{ Number(item.quantity) || 1 }}</td>
                    <td class="text-end align-middle fw-bold">{{ formatMoney(item.priceGross) }}</td>
                </tr>
            </tbody>
        </table>

        <!-- RAZEM -->
        <div class="d-flex justify-content-end mb-3">
            <table class="table table-bordered table-sm summary-table mb-0" style="width: 250px;">
                <tbody>
                    <tr>
                        <td class="bg-gray fw-bold py-1 px-2">Razem</td>
                        <td class="text-end fw-bold py-1 px-2">{{ formatMoney(totalAmount) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- PODSUMOWANIE KWOTOWE -->
        <div class="text-end mb-3">
            <div class="fs-5 fw-normal">
                Kwota: <span class="fw-bold">{{ formatMoney(totalAmount) }} {{ invoice.currency || 'PLN' }}</span>
            </div>
        </div>

        <!-- UWAGI -->
        <div class="mb-3 small p-2 border bg-light-subtle" v-if="invoice.additional_info || defaultUwagi">
            <strong>Uwagi:</strong> {{ invoice.additional_info || defaultUwagi }}
        </div>

        <!-- ZGODA NA PRZETWARZANIE DANYCH OSOBOWYCH W/G RODO -->
        <div class="rodo-section mb-3 text-justify fs-8 text-secondary">
            <div class="fw-bold text-dark mb-1">ZGODA NA PRZETWARZANIE DANYCH OSOBOWYCH W/G RODO</div>
            <div>
                Wyrażam zgodę na przetwarzanie moich danych osobowych przez firmę: {{ invoice.seller?.companyName ||
                '---' }} ul. {{ invoice.seller?.street || '---' }} {{ invoice.seller?.postalCode || '' }} {{
                    invoice.seller?.city || '' }} NIP: {{ invoice.seller?.nip || '---' }}; Regon: {{ invoice.seller?.regon
                || '---' }} w celu wystawienia faktury, rachunku, dowodu dostawy, formularza przyjęcia odpadów lub
                prowadzenia sprawozdawczości finansowej. Podaję dane osobowe dobrowolnie i oświadczam, że są one zgodne
                z prawdą. Rozumiem, że przysługuje mi prawo dostępu do treści swoich danych oraz prawo ich sprostowania,
                usunięcia, ograniczenia przetwarzania, jak również wniesienia sprzeciwu, prawo do cofnięcia zgody na ich
                przetwarzanie w dowolnym momencie bez wpływu na zgodność z prawem przetwarzania, którego dokonano na
                podstawie zgody wyrażonej przed jej cofnięciem.
            </div>
        </div>

        <!-- PŁATNOŚCI I KONTO -->
        <div class="row pt-2 border-top small">
            <div class="col-7">
                <div class="mb-1"><strong>Typ płatności:</strong> {{ invoice.payment?.[0]?.paymentType || 'Gotówka' }}
                </div>
                <div class="mb-2">
                    <strong>Zapłacone:</strong>
                    {{ formatMoney(totalAmount) }} {{ invoice.currency || 'PLN' }}<br>
                    <span class="fst-italic text-muted">{{ amountInWords }}</span>
                </div>
                <div v-if="invoice.seller?.bankAccount" class="mt-2">
                    <div class="fw-bold">{{ invoice.seller?.bankName || 'ING' }}</div>
                    <div>Konto PLN: {{ invoice.seller.bankAccount }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
    invoice: { type: Object, required: true }
})

const templateRoot = ref(null)

defineExpose({
    element: templateRoot
})

const carImageUrl = computed(() => {
    return props.invoice.car?.photoUrl ||
        props.invoice.carImage ||
        null
})

const formatName = (party) => {
    if (!party) return ''
    if (party.companyName) return party.companyName
    return `${party.firstName || ''} ${party.lastName || ''}`.trim()
}

const formatMoney = (val) => {
    if (!val && val !== 0) return '0.00'
    return Number(val).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const totalAmount = computed(() => {
    if (props.invoice.summaryBrutto) return Number(props.invoice.summaryBrutto)
    if (Array.isArray(props.invoice.positions)) {
        return props.invoice.positions.reduce((sum, item) => sum + (Number(item.priceGross) || 0), 0)
    }
    return 0
})

const defaultUwagi = 'Kupujący potwierdza swoim podpisem, że w dniu zakupu zapoznał się ze stanem technicznym pojazdu i z tego tytułu nie będzie rościł żadnych pretensji. Kupujący sprawdził wszystkie oznaczenia numerowe pojazdu, dowodu rejestracyjnego nie wnosząc żadnych zastrzeżeń. Sprzedający oświadcza, że jest płatnikiem podatku dochodowego oraz podatku VAT w Urzędzie Skarbowym.'

const amountInWords = computed(() => {
    const num = totalAmount.value
    if (num === 45600) return 'czterdzieści pięć tysięcy sześćset PLN zero gr'
    return `${num.toFixed(2)} PLN`
})
</script>

<style scoped>
.pdf-container,
.pdf-container * {
    color: #000000 !important;
    box-sizing: border-box;
}

.pdf-container {
    width: 210mm;
    padding: 10mm 15mm;
    font-family: Arial, sans-serif;
    font-size: 10.5px;
    background: #ffffff !important;
    color: #000000 !important;
    line-height: 1.3;
    isolation: isolate;
}

.header-dates {
    width: 280px;
}

.date-row {
    display: flex;
    align-items: baseline;
    font-size: 10.5px;
    margin-bottom: 2px;
}

.date-label {
    white-space: nowrap;
}

.date-dots {
    flex-grow: 1;
    border-bottom: 1px dotted #888888;
    margin: 0 4px;
}

.date-value {
    white-space: nowrap;
    font-weight: 500;
}

.bg-gray {
    background-color: #e2e2e2 !important;
}

.fs-7 {
    font-size: 10px;
}

.fs-8 {
    font-size: 8.5px;
}

.line-height-sm {
    line-height: 1.25;
}

.custom-table {
    font-size: 10px;
    border-color: #cccccc;
    background-color: #ffffff;
}

.custom-table th {
    font-weight: normal;
    padding: 4px 6px;
    background-color: #e2e2e2 !important;
}

.custom-table td {
    padding: 5px 6px;
    background-color: #ffffff !important;
}

.summary-table td {
    padding: 3px 6px;
    border-color: #cccccc !important;
}

.pdf-container .card {
    background-color: #ffffff !important;
    border-color: #cccccc !important;
}

.pdf-container .photo-box {
    background-color: #f8f9fa !important;
}
</style>